'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { IconButton } from '../../../components/icon-button';
import { Input } from '../../../components/input';
import { Collapsible } from '../../../components/collapsible';
import {
  HeadingItem,
  MetaDefault,
  BodyDefault,
} from '../../../components/typography';
import { ConfirmationDialog } from '../../confirmation-dialog/ConfirmationDialog';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Loading03Icon,
  Edit03Icon,
  Search01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { ConversationHistoryRow } from './ConversationHistoryRow';
import { groupConversationsByDate } from './group-conversations-by-date';
import { matchesConversationSearch } from './matches-conversation-search';
import type {
  ConversationHistoryDraftEntry,
  ConversationHistoryPanelProps,
  ConversationHistorySavedEntry,
} from './types';

export const COPILOT_CHAT_PANEL_WIDTH_PX = 335;
export const CONVERSATION_HISTORY_PANEL_WIDTH_PX = 228;
const PANEL_WIDTH = `${CONVERSATION_HISTORY_PANEL_WIDTH_PX}px`;

type DraftHistoryRowProps = {
  title: string;
  isActive: boolean;
  isStreaming?: boolean;
  updatedAt?: string;
  formatUpdatedAt: (isoString: string) => string;
  deleteAriaLabel: string;
  onSelect: () => void;
  onDelete: () => void;
};

function DraftHistoryRow({
  title,
  isActive,
  isStreaming,
  updatedAt,
  formatUpdatedAt,
  deleteAriaLabel,
  onSelect,
  onDelete,
}: DraftHistoryRowProps) {
  const [isHovered, setIsHovered] = useState(false);
  const showMeta = isHovered;

  return (
    <Box
      role="button"
      tabIndex={0}
      w="full"
      minW={0}
      onClick={onSelect}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      textAlign="left"
      border="none"
      borderRadius="md"
      cursor="pointer"
      bg={isActive ? 'bg.muted' : 'transparent'}
      _hover={{ bg: 'bg.subtle' }}
      px="2"
      py="1.5"
    >
      <HStack gap="2" w="full" minW={0} alignItems="center" overflow="hidden">
        <Box flexShrink={0} display="inline-flex">
          {isStreaming ? (
            <Box
              as="span"
              display="inline-flex"
              flexShrink={0}
              animationStyle="spin"
              animationDuration="1.5s"
            >
              <Loading03Icon size={12} color={token('colors.terra.a9')} />
            </Box>
          ) : (
            <Edit03Icon size={12} opacity={0.7} />
          )}
        </Box>
        <Box flex="1" minW={0} overflow="hidden">
          <BodyDefault as="span" display="block" truncate>
            {title}
          </BodyDefault>
        </Box>
        {showMeta ? (
          <HStack gap="1" flexShrink={0} alignItems="center">
            {updatedAt ? (
              <MetaDefault color="fg.muted" whiteSpace="nowrap">
                {formatUpdatedAt(updatedAt)}
              </MetaDefault>
            ) : null}
            <IconButton
              variant="link"
              size="xs"
              aria-label={deleteAriaLabel}
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
              color="fg.muted"
              _hover={{ color: 'fg.default' }}
            >
              <Cancel01Icon size={10} />
            </IconButton>
          </HStack>
        ) : null}
      </HStack>
    </Box>
  );
}

function DraftsSection({
  drafts,
  activeId,
  labels,
  onSelect,
  onRequestDelete,
}: {
  drafts: ConversationHistoryDraftEntry[];
  activeId: string | null;
  labels: ConversationHistoryPanelProps['labels'];
  onSelect: (id: string) => void;
  onRequestDelete: (id: string) => void;
}) {
  if (drafts.length === 0) {
    return null;
  }

  return (
    <VStack gap="1" alignItems="stretch" w="full">
      <MetaDefault
        color="fg.muted"
        fontWeight="semibold"
        textTransform="uppercase"
        letterSpacing="wider"
        px="2"
        py="1"
      >
        {labels.draftsSection}
      </MetaDefault>
      {drafts.map((draft) => {
        const preview = draft.subtitle ?? draft.title;

        return (
          <DraftHistoryRow
            key={draft.id}
            title={labels.draftTitle(preview)}
            isActive={activeId === draft.id}
            isStreaming={draft.isStreaming}
            updatedAt={draft.updatedAt}
            formatUpdatedAt={labels.formatUpdatedAt}
            deleteAriaLabel={labels.closeDraftAriaLabel}
            onSelect={() => onSelect(draft.id)}
            onDelete={() => onRequestDelete(draft.id)}
          />
        );
      })}
    </VStack>
  );
}

function ConversationGroupSection({
  label,
  conversations,
  activeId,
  labels,
  onSelect,
  onRequestDelete,
  onRename,
}: {
  label: string;
  conversations: ConversationHistorySavedEntry[];
  activeId: string | null;
  labels: ConversationHistoryPanelProps['labels'];
  onSelect: (id: string) => void;
  onRequestDelete: (id: string) => void;
  onRename: (id: string, title: string) => void | Promise<void>;
}) {
  const tDrawer = useTranslations('designSystem.customDrawer');
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Collapsible.Root
      open={isOpen}
      onOpenChange={(details) => setIsOpen(details.open)}
    >
      <VStack gap="1" alignItems="stretch" w="full">
        <Collapsible.Trigger asChild>
          <Box
            as="button"
            cursor="pointer"
            bg="transparent"
            border="none"
            w="full"
            px="2"
            py="1"
            aria-label={isOpen ? tDrawer('collapse') : tDrawer('expand')}
          >
            <HStack gap="1" justifyContent="flex-start">
              <MetaDefault
                color="fg.muted"
                fontWeight="semibold"
                textTransform="uppercase"
                letterSpacing="wider"
              >
                {label}
              </MetaDefault>
              <Box as="span" color="fg.muted" display="inline-flex">
                {isOpen ? (
                  <ArrowDown01Icon size={12} />
                ) : (
                  <ArrowRight01Icon size={12} />
                )}
              </Box>
            </HStack>
          </Box>
        </Collapsible.Trigger>
        <Collapsible.Content>
          <VStack gap="0" alignItems="stretch" w="full">
            {conversations.map((conversation) => (
              <ConversationHistoryRow
                key={conversation.id}
                title={conversation.title ?? labels.untitledConversation}
                isActive={activeId === conversation.id}
                isStreaming={conversation.isStreaming}
                updatedAt={conversation.updatedAt}
                labels={labels}
                onSelect={() => onSelect(conversation.id)}
                onDelete={() => onRequestDelete(conversation.id)}
                onRename={(title) => onRename(conversation.id, title)}
              />
            ))}
          </VStack>
        </Collapsible.Content>
      </VStack>
    </Collapsible.Root>
  );
}

export function ConversationHistoryPanel({
  isOpen,
  onClose,
  drafts = [],
  conversations,
  activeId,
  labels,
  onSelect,
  onDelete,
  onRename,
}: ConversationHistoryPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPendingDeleteId(null);
    }
  }, [isOpen]);
  const filteredDrafts = useMemo(() => {
    if (!searchQuery.trim()) {
      return drafts;
    }

    const query = searchQuery.trim();

    return drafts.filter((draft) =>
      matchesConversationSearch({
        query,
        title: draft.title,
        subtitle: draft.subtitle,
      }),
    );
  }, [drafts, searchQuery]);
  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) {
      return conversations;
    }

    const query = searchQuery.trim();

    return conversations.filter((conversation) =>
      matchesConversationSearch({
        query,
        title: conversation.title ?? '',
        description: conversation.description,
      }),
    );
  }, [conversations, searchQuery]);
  const groupedConversations = useMemo(
    () => groupConversationsByDate(filteredConversations),
    [filteredConversations],
  );
  const hasHistoryResults =
    filteredDrafts.length > 0 || filteredConversations.length > 0;

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <VStack
        w={PANEL_WIDTH}
        minW={PANEL_WIDTH}
        maxW={PANEL_WIDTH}
        h="full"
        gap="2"
        alignItems="stretch"
        borderLeftWidth="1px"
        borderColor="border.subtle"
        bg="bg.canvas"
        flexShrink={0}
        pt="1"
        px="3"
        pb="3"
      >
        <HStack
          h="8"
          justifyContent="space-between"
          alignItems="center"
          flexShrink={0}
        >
          <HeadingItem>{labels.panelTitle}</HeadingItem>
          <IconButton
            variant="ghost"
            size="xs"
            aria-label={labels.closePanelAriaLabel}
            onClick={onClose}
            color="fg.muted"
            _hover={{ color: 'fg.default', bg: 'transparent' }}
          >
            <Cancel01Icon size={14} />
          </IconButton>
        </HStack>

        <Box flexShrink={0}>
          <Box position="relative">
            <Box
              position="absolute"
              left="2"
              top="50%"
              transform="translateY(-50%)"
              color="fg.muted"
              pointerEvents="none"
            >
              <Search01Icon size={12} />
            </Box>
            <Input
              placeholder={labels.searchPlaceholder}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              size="xs"
              pl="7"
              borderRadius="md"
            />
          </Box>
        </Box>

        <VStack flex="1" minH={0} overflowY="auto" gap="2" alignItems="stretch">
          {!hasHistoryResults ? (
            <Box p="3" textAlign="center">
              <MetaDefault color="fg.muted">
                {searchQuery.trim() ? labels.noResults : labels.empty}
              </MetaDefault>
            </Box>
          ) : (
            <>
              <DraftsSection
                drafts={filteredDrafts}
                activeId={activeId}
                labels={labels}
                onSelect={(id) => onSelect(id, 'draft')}
                onRequestDelete={(id) => onDelete(id, 'draft')}
              />
              {groupedConversations.map((group) => (
                <ConversationGroupSection
                  key={group.labelKey}
                  label={labels.dateGroups[group.labelKey]}
                  conversations={group.conversations}
                  activeId={activeId}
                  labels={labels}
                  onSelect={(id) => onSelect(id, 'conversation')}
                  onRequestDelete={(id) => setPendingDeleteId(id)}
                  onRename={onRename}
                />
              ))}
            </>
          )}
        </VStack>
      </VStack>

      <ConfirmationDialog
        isOpen={pendingDeleteId !== null}
        onOpenChange={(details) => {
          if (!details.open) {
            setPendingDeleteId(null);
          }
        }}
        onConfirm={() => {
          if (!pendingDeleteId) {
            return;
          }

          onDelete(pendingDeleteId, 'conversation');
          setPendingDeleteId(null);
        }}
        variant="destructive"
        title={labels.deleteConversationConfirmTitle}
        description={labels.deleteConversationConfirmDescription}
        confirmText={labels.deleteConfirmLabel}
      />
    </>
  );
}
